// components/Tabs/AgoraMimamoriMeeting.tsx
// みまもり会議専用のAgoraコンポーネント
// 緊急みまもり機能に特化した実装で、通常会議とは独立して管理

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AgoraRTC, { IAgoraRTCClient } from 'agora-rtc-sdk-ng';
import { Button, Input, Typography, Space, message, Card, Alert } from 'antd';
import axios from 'axios';
import { getAuthBearerHeaders } from '../../firebase/firebase';
import { 
  RemoteUser, 
  Participant, 
  MeetingType,
  AGORA_CONFIG,
  MimamoriMeetingInfo
} from './web_parts/AgoraTypes';
import {
  useWindowSize,
  parseUid,
  fetchMeetingData,
  fetchInitialData,
  calculateOptimalVideoSize,
  getParticipantName,
  getNonParticipants,
  playRemoteVideo,
  getCollectionName,
  markChannelClosedInFirestore,
  debugLog
} from './web_parts/AgoraBase';

const { Title } = Typography;

/**
 * みまもり会議専用のAgoraコンポーネント
 * 緊急みまもり機能に特化し、通常会議とは分離した実装
 * - 自動参加機能
 * - 緊急通知表示
 * - みまもり専用UI
 */
const AgoraMimamoriMeeting: React.FC = () => {
  // URLパラメータの取得
  const { channelId } = useParams<{ channelId: string }>();
  const [searchParams] = useSearchParams();
  const isTimingDebugMode =
    searchParams.get('debug') === '1' ||
    searchParams.get('timingDebug') === '1' ||
    process.env.REACT_APP_TIMING_DEBUG === 'true' ||
    (typeof window !== 'undefined' && window.localStorage.getItem('caretablet:timingDebug') === '1');
  
  // みまもり会議パラメータ
  const type = searchParams.get("type");
  const userNameFromUrl = searchParams.get("name");
  const uidFromUrl = searchParams.get("uid");
  
  // みまもり会議かどうかの判定
  const isMimamoriMeeting = type === 'mimamori';
  const skipNameInput = !!userNameFromUrl;
  
  // 基本状態管理
  const [name, setName] = useState<string>(userNameFromUrl || 'みまもり管理者');
  const [uid, setUid] = useState<number | null>(null);
  const [joined, setJoined] = useState<boolean>(false);
  const [isJoining, setIsJoining] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [cameraDeviceIds, setCameraDeviceIds] = useState<string[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState<number>(0);
  
  // 会議・参加者情報
  const [meetingName, setMeetingName] = useState<string>('緊急みまもり会議');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [meetingInfo, setMeetingInfo] = useState<MimamoriMeetingInfo | null>(null);
  const [joinTiming, setJoinTiming] = useState<{
    status: 'idle' | 'running' | 'success' | 'error';
    totalElapsedMs?: number;
    joinMs?: number;
    tracksMs?: number;
    camerasMs?: number;
    publishMs?: number;
  }>({ status: 'idle' });
  
  // リモートユーザー関連
  const [remoteUsers, setRemoteUsers] = useState<RemoteUser[]>([]);
  const [speakingUser, setSpeakingUser] = useState<string | null>(null);
  const [remoteAudioStates, setRemoteAudioStates] = useState<{ [uid: string]: boolean }>({});
  const [remoteVideoStates, setRemoteVideoStates] = useState<{ [uid: string]: boolean }>({});
  
  // 退室状態管理
  const [hasLeft, setHasLeft] = useState(false);
  const [leftReason, setLeftReason] = useState<'manual' | 'error'>('manual');
  const [shouldPreventAutoJoin, setShouldPreventAutoJoin] = useState(false);

  // メイン画面にはAndroidのユーザーだけを表示するためのフラグ
  const [displayMode, setDisplayMode] = useState<'speaking' | 'android_priority'>('android_priority');

  // DOM参照
  const localVideoRef = useRef<HTMLDivElement>(null);
  const mainVideoRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const localTracks = useRef<{ videoTrack: any; audioTrack: any } | null>(null);
  const remoteVideoRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  // レスポンシブデザイン
  const { width, height, isMobilePortrait, isWideLayout } = useWindowSize();
  const [mainHeight, setMainHeight] = useState('100vh');

  // カメラオフアイコンの読み込み
  const cameraOffIcon = require("../../assets/images/icon_camera_off.png");

  /**
   * 自動入室の制御（みまもり会議専用）
   */
  useEffect(() => {
    if (shouldPreventAutoJoin || hasLeft) {
      debugLog('自動入室を防止', { shouldPreventAutoJoin, hasLeft });
      return;
    }
    
    if (skipNameInput && userNameFromUrl && !joined && !isJoining && uid !== null) {
      debugLog('みまもり会議の自動入室を実行', { name: userNameFromUrl, uid });
      joinMeeting();
    }
  }, [skipNameInput, userNameFromUrl, joined, isJoining, uid, shouldPreventAutoJoin, hasLeft]);

  /**
   * 初期データの取得（名前とUID）- みまもり会議専用
   */
  useEffect(() => {
    const loadInitialData = async () => {
      if (!channelId) return;
      
      try {
        const { name: initialName, uid: initialUid } = await fetchInitialData(
          channelId,
          getCollectionName(MeetingType.MIMAMORI),
          userNameFromUrl,
          uidFromUrl
        );
        
        setName(initialName);
        setUid(initialUid);
        
        debugLog('みまもり会議の初期データ設定完了', { 
          name: initialName, 
          uid: initialUid,
          fromUrl: { userNameFromUrl, uidFromUrl }
        });
      } catch (error) {
        console.error('初期データ取得失敗:', error);
        // エラー時でも基本的なUIDを設定
        const fallbackUid = uidFromUrl ? parseUid(uidFromUrl).numericUid : Math.floor(Math.random() * 1000000);
        setUid(fallbackUid);
      }
    };

    loadInitialData();
  }, [channelId, userNameFromUrl, uidFromUrl]);

  /**
   * 会議情報と参加者情報の取得（みまもり会議専用）
   */
  useEffect(() => {
    const loadMeetingData = async () => {
      if (!channelId || uid === null) return;

      try {
        const headers = await getAuthBearerHeaders();
        const response = await axios.get("/api/getMimamoriParticipantsPublic", {
          params: { channelId },
          headers,
        });
        const fetchedMeetingName = String(response.data?.meetingName || "緊急みまもり会議");
        const fetchedParticipants = Array.isArray(response.data?.participants)
          ? response.data.participants
              .map((p: { uid: number; name: string; serialNumber?: string }) => ({
                uid: Number(p.uid),
                name: p.name || `参加者(${p.uid})`,
                serialNumber: typeof p.serialNumber === "string" ? p.serialNumber : undefined,
              }))
              .filter((p: Participant) => p.uid !== uid)
          : [];

        setMeetingName(fetchedMeetingName);
        setParticipants(fetchedParticipants);
        
        debugLog('みまもり会議データ取得完了', { 
          meetingName: fetchedMeetingName, 
          participantCount: fetchedParticipants.length 
        });
      } catch (error) {
        console.error('みまもり会議データ取得失敗:', error);
      }
    };

    loadMeetingData();
  }, [channelId, uid, remoteUsers]);

  /**
   * コンポーネントのクリーンアップ - beforeunload対応版
   */
  useEffect(() => {
    return () => {
      if (clientRef.current && joined) {
        debugLog('みまもり会議コンポーネントクリーンアップ: 会議から退出');
        // 強制退室フラグをtrueに設定
        leaveMeeting(true);
      }
    };
  }, [channelId, joined]); // joinedも依存配列に追加

  /**
   * ローカルビデオの再生
   */
  useEffect(() => {
    if (joined && localVideoRef.current && localTracks.current?.videoTrack) {
      debugLog('ローカルビデオをレンダリング中');
      localTracks.current.videoTrack.play(localVideoRef.current, { fit: "contain" });
    }
  }, [joined]);

  /**
   * 発言者検出の処理
   */
  useEffect(() => {
    if (!joined || !clientRef.current) return;

    const detectSpeaking = () => {
      const users = [...(clientRef.current?.remoteUsers || [])];

      // みまもり会議の表示モード判定
      if (displayMode === 'android_priority') {
        // Android側ユーザー（uid=99999999）を優先表示
        const androidUser = users.find(user => Number(user.uid) === 99999999);
        if (androidUser) {
          setSpeakingUser(String(androidUser.uid));
          return; // Android側ユーザーが見つかったら、これを優先表示
        }
      }      

      let maxAudioLevel = 0;
      let speakingUid: string | null = null;

      users.forEach((user) => {
        if (user.audioTrack) {
          const audioLevel = user.audioTrack.getVolumeLevel();
          if (audioLevel > maxAudioLevel) {
            maxAudioLevel = audioLevel;
            speakingUid = String(user.uid);
          }
        }
      });

      if (maxAudioLevel > 0.1) {
        setSpeakingUser(speakingUid);
      } else if (!speakingUser && users.length > 0) {
        // 発言者がいない場合の処理
        if (displayMode === 'android_priority') {
          // Android側ユーザーを最優先
          const androidUser = users.find(user => Number(user.uid) === 99999999);
          setSpeakingUser(androidUser ? String(androidUser.uid) : String(users[0].uid));
        } else {
          setSpeakingUser(String(users[0].uid));
        }
      } else if (users.length === 0) {
        setSpeakingUser(null);
      }
    };
    
    const interval = setInterval(detectSpeaking, 500);
    return () => clearInterval(interval);
  }, [joined, remoteUsers, speakingUser, displayMode]);

  /**
   * メインビデオの再生
   */
  useEffect(() => {
    if (!mainVideoRef.current || !speakingUser) return;

    const playMainVideo = () => {
      if (!mainVideoRef.current) return;

      const currentMainUser = remoteUsers.find((u) => String(u.uid) === speakingUser);
      if (currentMainUser && currentMainUser.videoTrack) {
        currentMainUser.videoTrack.play(mainVideoRef.current, { fit: "contain" });
      }
    };

    playMainVideo();
  }, [speakingUser, remoteUsers]);

  /**
   * リモートビデオの再生管理
   */
    useEffect(() => {
    remoteUsers.forEach((user) => {
        if (String(user.uid) !== speakingUser) { // メイン表示中以外のユーザーのビデオを再生
        const container = remoteVideoRefs.current.get(String(user.uid));
        if (container) { // コンテナが取得できているか確認
            playRemoteVideo(user, container);
        } else {
            // コンテナが見つからない場合、次のレンダリングサイクルで設定されるのを待つか、エラーログを出す
            console.warn(`ビデオコンテナが見つかりません (UID: ${user.uid})。次の更新で再試行されます。`);
        }
        }
    });

    debugLog('リモートユーザー状態（みまもり）', 
        remoteUsers.map((user) => ({ uid: user.uid, hasVideo: !!user.videoTrack, isSpeaking: String(user.uid) === speakingUser }))
    );
    }, [remoteUsers, speakingUser]); // speakingUserの変更でも再評価する
    
  /**
   * ウィンドウサイズが変更されたときにビデオサイズを再計算
   */
  useEffect(() => {
    if (joined && mainVideoRef.current) {
      updateVideoSize();
    }
  }, [width, height, joined]);

  /**
   * リモート参加者の状態を追跡(ミュート、カメラオフ)
   */
  useEffect(() => {
    if (!joined || !clientRef.current) return;

    // 初期状態の設定
    const initialAudioStates: { [uid: string]: boolean } = {};
    const initialVideoStates: { [uid: string]: boolean } = {};
    remoteUsers.forEach(user => {
      initialAudioStates[user.uid] = !!user.audioTrack;
      initialVideoStates[user.uid] = !!user.videoTrack;
    });
    setRemoteAudioStates(initialAudioStates);
    setRemoteVideoStates(initialVideoStates);
    
    // getVolumeLevel() ベースの発言者検出に必要（volume-indicator のコールバック引数は SDK 版で異なるため、誤ったハンドラは登録しない）
    clientRef.current.enableAudioVolumeIndicator();
  }, [joined, remoteUsers]); // remoteUsersの変更時にも状態を再設定

  /**
   * ページ離脱時の確認処理（F5リフレッシュ対応）
   */
  useEffect(() => {
    // 会議参加中のみイベントリスナーを設定
    if (!joined) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // みまもり会議中の離脱確認メッセージ
      const message = 'みまもり会議を退室しようとしています。よろしいですか？';
      
      // 標準的な離脱確認の設定
      event.preventDefault();
      event.returnValue = message; // Chrome, Firefox対応
      return message; // Safari対応
    };

    const handleUnload = () => {
      // ページが実際にアンロードされる時の処理
      // 「はい」が選択された場合の退室処理
      if (joined && clientRef.current) {
        // 同期的な退室処理（非同期処理は使用不可）
        try {
          // トラックを停止
          localTracks.current?.audioTrack?.stop();
          localTracks.current?.videoTrack?.stop();
          localTracks.current?.audioTrack?.close();
          localTracks.current?.videoTrack?.close();
          
          // クライアントから退出（同期処理のみ可能）
          clientRef.current.leave();
          
          console.log('みまもり会議: 強制退室処理完了');
        } catch (error) {
          console.error('みまもり会議: 強制退室処理エラー:', error);
        }
      }
    };

    // イベントリスナーを追加
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    // クリーンアップ関数
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [joined]); // joinedの状態が変わった時に再設定

  /**
   * 画面の高さ計算
   */
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const headerHeight = headerRef.current.getBoundingClientRect().height;
        // ここでの48pxは、全体のpadding (上下24pxずつ) を想定しているか、
        // あるいは他の固定フッター要素などの高さを考慮している可能性がある。
        // StandardMeetingとMimamoriMeetingでこの値が共通であるか確認。
        const newHeight = `calc(100vh - ${headerHeight}px - 48px)`; 
        setMainHeight(newHeight);
      }
    };

    updateHeight(); // 初期計算
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [joined]); // joined状態が変わった時にも高さ再計算 (ヘッダー表示有無が変わるため)

  /**
   * ビデオサイズ更新関数
   */
  const updateVideoSize = () => {
    if (mainVideoRef.current) {
      const optimalSize = calculateOptimalVideoSize(mainVideoRef.current);
      Object.assign(mainVideoRef.current.style, optimalSize);
    }
  };

  /**
   * みまもり会議参加処理
   */
  const joinMeeting = async () => {
    if (!channelId) {
      message.error('チャンネルIDが指定されていません。');
      return;
    }

    if (uid === null) {
      message.error('UIDが取得できませんでした。');
      return;
    }

    if (joined || clientRef.current || isJoining) {
      debugLog('既にみまもり会議に参加済みです。');
      return;
    }

    debugLog('みまもり会議に参加中', { channelId, uid, name });
    setJoinTiming({ status: 'running' });
    const joinStartMs = performance.now();
    const logJoinStep = (step: string, stepStartMs?: number) => {
      const totalElapsedMs = Math.round(performance.now() - joinStartMs);
      const stepElapsedMs = stepStartMs == null ? undefined : Math.round(performance.now() - stepStartMs);
      setJoinTiming((prev) => {
        const next = { ...prev, totalElapsedMs };
        if (step === 'agora:client.join:done') next.joinMs = stepElapsedMs;
        if (step === 'agora:createMicrophoneAndCameraTracks:done') next.tracksMs = stepElapsedMs;
        if (step === 'agora:getCameras:done') next.camerasMs = stepElapsedMs;
        if (step === 'agora:publish:done') next.publishMs = stepElapsedMs;
        if (step === 'joinMeeting:success') next.status = 'success';
        if (step === 'joinMeeting:error') next.status = 'error';
        return next;
      });
      debugLog('[join-profiler][mimamori]', {
        step,
        totalElapsedMs,
        stepElapsedMs,
        channelId,
        uid,
      });
    };
    logJoinStep('joinMeeting:start');
    setIsJoining(true);
    setShouldPreventAutoJoin(true); // 手動での参加後は自動再入室を防ぐ

    const client = AgoraRTC.createClient({ 
      mode: AGORA_CONFIG.MODE, 
      codec: AGORA_CONFIG.CODEC 
    });
    clientRef.current = client;

    client.on('user-published', async (user, mediaType) => {
    try {
        await client.subscribe(user, mediaType);
        debugLog(`みまもり会議: ユーザー ${user.uid} の ${mediaType} をサブスクライブしました`);

        setRemoteUsers((prevUsers) => {
          // ユーザーが存在しない場合のみ追加
          if (!prevUsers.find(u => u.uid === user.uid)) {
            return [...prevUsers, user];
          }
          // 既に存在するユーザー情報を更新 (mediaTypeに応じてトラック情報を更新)
          return prevUsers.map(u => u.uid === user.uid ? user : u);
        });
        
        if (mediaType === 'video') {
          setRemoteVideoStates(prev => ({ ...prev, [user.uid]: true }));
          // メイン表示中でなければ、かつコンテナがあれば再生
          if (String(user.uid) !== speakingUser) {
            const container = remoteVideoRefs.current.get(String(user.uid));
            if (container && user.videoTrack) {
              playRemoteVideo(user, container);
            }
          }
        }

        if (mediaType === 'audio') {
          setRemoteAudioStates(prev => ({ ...prev, [user.uid]: true }));
          user.audioTrack?.play();
        }
    } catch (err) {
        console.error(`みまもり会議サブスクライブエラー (UID: ${user.uid}):`, err);
        message.error(`ユーザー ${user.uid} のメディア受信に失敗しました。`);
    }
    });

    client.on('user-unpublished', (user, mediaType) => {
      debugLog(`みまもり会議: ユーザー ${user.uid} の ${mediaType} が未公開になりました`);
      
      if (mediaType === 'video') {
        setRemoteVideoStates(prev => ({ ...prev, [user.uid]: false }));
      } else if (mediaType === 'audio') {
        setRemoteAudioStates(prev => ({ ...prev, [user.uid]: false }));
      }

      // トラック情報をクリアするためにユーザー情報を更新
      setRemoteUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.uid === user.uid
            ? { 
                ...u, 
                videoTrack: mediaType === 'video' ? undefined : u.videoTrack,
                audioTrack: mediaType === 'audio' ? undefined : u.audioTrack
              }
            : u
        ).filter(u => u.videoTrack || u.audioTrack || u.uid === user.uid) // 不要になったユーザー情報を整理する検討も
      );      
    });

    client.on('user-left', (user) => {
      debugLog(`みまもり会議: ユーザー ${user.uid} が退出しました`);
      setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
      remoteVideoRefs.current.delete(String(user.uid)); // 参照も削除

      // 関連する状態もクリア
      setRemoteVideoStates(prev => {
        const newState = { ...prev };
        delete newState[user.uid];
        return newState;
      });
      setRemoteAudioStates(prev => {
        const newState = { ...prev };
        delete newState[user.uid];
        return newState;
      });      
      // もし退出したユーザーが発言者だったら、発言者をリセット
      if (speakingUser === String(user.uid)) {
        setSpeakingUser(null); 
      }
    });

    try {
      debugLog('みまもり会議: チャンネルに接続中', { uid: typeof uid });
      const numericUid = typeof uid === 'number' ? uid : parseUid(String(uid)).numericUid;
      const joinStepStart = performance.now();
      await client.join(AGORA_CONFIG.APP_ID, channelId, AGORA_CONFIG.TOKEN, numericUid);
      debugLog('みまもり会議: チャンネル接続成功', { uid: numericUid });
      logJoinStep('agora:client.join:done', joinStepStart);
      
      try {
        const tracksStepStart = performance.now();
        const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
        localTracks.current = { audioTrack, videoTrack };
        logJoinStep('agora:createMicrophoneAndCameraTracks:done', tracksStepStart);
        const cameraListStepStart = performance.now();
        await loadCameraDevices();
        logJoinStep('agora:getCameras:done', cameraListStepStart);
        
        // nullでないトラックのみを公開
        const tracksToPublish = [audioTrack, videoTrack].filter((track): track is NonNullable<typeof track> => !!track);
        const publishStepStart = performance.now();
        if (tracksToPublish.length > 0) {
          await client.publish(tracksToPublish);
          logJoinStep('agora:publish:done', publishStepStart);
          debugLog('みまもり会議: 音声・映像トラックを公開しました');
        } else {
          debugLog('みまもり会議: 公開するローカルトラックがありません（マイク・カメラ非許可など）');
        }
      } catch (trackError) {
        console.error('みまもり会議: カメラ/マイクの初期化エラー:', trackError);
        logJoinStep('agora:tracks_or_publish:error');
        message.warning('カメラまたはマイクの初期化に失敗しました。設定を確認してください。');
        localTracks.current = { audioTrack: null, videoTrack: null }; // エラー時も参照を初期化
      }
      
      setJoined(true);
      debugLog('みまもり会議参加完了');
      logJoinStep('joinMeeting:success');
      setTimeout(updateVideoSize, 500); // 参加後にビデオサイズを確実に更新
    } catch (err) {
      console.error('みまもり会議参加エラー:', err);
      message.error('みまもり会議への参加に失敗しました。');
      
      if (localTracks.current?.audioTrack) localTracks.current.audioTrack.close();
      if (localTracks.current?.videoTrack) localTracks.current.videoTrack.close();
      localTracks.current = null;
      clientRef.current = null; // クライアント参照もクリア
      
      setLeftReason('error');
      setHasLeft(true);
      logJoinStep('joinMeeting:error');
    } finally {
      setIsJoining(false);
    }
  };

  /**
   * ミュート切り替え
   */
  const toggleMute = () => {
    if (localTracks.current?.audioTrack) {
      const currentMuted = !localTracks.current.audioTrack.enabled;
      localTracks.current.audioTrack.setEnabled(currentMuted);
      setIsMuted(!currentMuted);
    }
  };

  /**
   * ビデオ切り替え
   */
  const toggleVideo = () => {
    if (localTracks.current?.videoTrack) {
      const currentVideoOff = !localTracks.current.videoTrack.enabled;
      localTracks.current.videoTrack.setEnabled(currentVideoOff);
      setIsVideoOff(!currentVideoOff);
    }
  };

  /**
   * 利用可能なカメラデバイス一覧を取得
   */
  const loadCameraDevices = async () => {
    try {
      const cameras = await AgoraRTC.getCameras();
      setCameraDeviceIds(cameras.map((camera) => camera.deviceId));
    } catch (error) {
      console.error('カメラデバイスの取得に失敗しました:', error);
    }
  };

  /**
   * カメラ切り替え
   */
  const switchCamera = async () => {
    try {
      const videoTrack = localTracks.current?.videoTrack;
      if (!videoTrack) return;

      let devices = cameraDeviceIds;
      if (devices.length <= 1) {
        const cameras = await AgoraRTC.getCameras();
        devices = cameras.map((camera) => camera.deviceId);
        setCameraDeviceIds(devices);
      }

      if (devices.length <= 1) {
        message.info('切り替え可能なカメラが見つかりません');
        return;
      }

      const nextIndex = (currentCameraIndex + 1) % devices.length;
      await videoTrack.setDevice(devices[nextIndex]);
      setCurrentCameraIndex(nextIndex);
      message.success('カメラを切り替えました');
    } catch (error) {
      console.error('カメラ切り替えに失敗しました:', error);
      message.error('カメラ切り替えに失敗しました');
    }
  };

  /**
   * 退室処理（みまもり会議専用）- beforeunload対応版
   */
  const leaveMeeting = async (isForced: boolean = false) => {
    debugLog('みまもり会議: 退室処理を開始', { isForced });
    
    if (clientRef.current) {
      try {
        localTracks.current?.audioTrack?.stop();
        localTracks.current?.audioTrack?.close();
        localTracks.current?.videoTrack?.stop();
        localTracks.current?.videoTrack?.close();
        
        await clientRef.current.leave();
        debugLog('みまもり会議: チャンネルから退出完了');

        if (channelId) {
          await markChannelClosedInFirestore(channelId, 'agora_mimamori');
        }
        
        // 強制退室でない場合のみ状態更新
        if (!isForced) {
          setLeftReason('manual');
        }
        
      } catch (err) {
        console.error('みまもり会議: 退室エラー:', err);
        if (!isForced) {
          message.error('退室処理に失敗しました。');
          setLeftReason('error');
        }
      } finally {
        // 成功・失敗に関わらず状態をリセット
        setJoined(false);
        setRemoteUsers([]);
        setSpeakingUser(null);
        remoteVideoRefs.current.clear();
        setRemoteAudioStates({});
        setRemoteVideoStates({});
        
        clientRef.current = null;
        localTracks.current = null;
        
        if (!isForced) {
          setHasLeft(true);
        }
        
        debugLog('みまもり会議: 退室関連状態リセット完了');
      }
    } else {
      debugLog('みまもり会議: クライアントが存在しないため、状態のみリセット');
      if (!isForced) {
        setHasLeft(true);
        setLeftReason('manual');
      }
    }
  };

  // メインエリアのスタイル (StandardMeetingと共通化されているか確認)
  const mainAreaStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: isWideLayout ? 'row' : 'column',
    gap: '8px', // 要素間のギャップ
    height: mainHeight,
    minHeight: 0, // flexアイテムが縮小できるように
    overflow: 'hidden', // はみ出した内容は隠す
    maxWidth: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // 全体がはみ出さないように
        boxSizing: 'border-box',
        padding: '24px', // 全体のpadding (StandardMeetingと共通か確認)
      }}
    >
      {/* ヘッダー部分（みまもり会議専用） */}
      <div ref={headerRef}> {/* このdivの高さがheaderHeightとして計算される */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <Title level={3} style={{ color: '#ff4d4f', margin: 0 }}> {/* みまもり特有のスタイル */}
            🚨 {meetingName}
          </Title>
          {joined && (
            <Button danger onClick={() => leaveMeeting(false)}> {/* falseを明示的に渡す */}
              みまもり終了
            </Button>
          )}
        </div>

        {joined && (
          <Space style={{ marginTop: 16, marginBottom: 16 }}>
            <Button onClick={toggleMute}>
              {isMuted ? 'ミュート解除' : 'ミュート'}
            </Button>
            <Button onClick={toggleVideo}>
              {isVideoOff ? 'カメラON' : 'カメラOFF'}
            </Button>
            <Button onClick={switchCamera} disabled={cameraDeviceIds.length <= 1}>
              カメラ切替
            </Button>
          </Space>
        )}
        {isTimingDebugMode && joinTiming.status !== 'idle' && (
          <div
            style={{
              marginTop: 8,
              marginBottom: 8,
              padding: '8px 12px',
              borderRadius: 6,
              background: '#fafafa',
              border: '1px solid #d9d9d9',
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            <div>
              接続計測: {joinTiming.status === 'running' ? '計測中' : joinTiming.status === 'success' ? '完了' : 'エラー'}
            </div>
            <div>合計: {joinTiming.totalElapsedMs != null ? `${(joinTiming.totalElapsedMs / 1000).toFixed(2)}秒` : '-'}</div>
            <div>Agora Join: {joinTiming.joinMs != null ? `${(joinTiming.joinMs / 1000).toFixed(2)}秒` : '-'}</div>
            <div>カメラ/マイク取得: {joinTiming.tracksMs != null ? `${(joinTiming.tracksMs / 1000).toFixed(2)}秒` : '-'}</div>
            <div>カメラ一覧取得: {joinTiming.camerasMs != null ? `${(joinTiming.camerasMs / 1000).toFixed(2)}秒` : '-'}</div>
            <div>Publish: {joinTiming.publishMs != null ? `${(joinTiming.publishMs / 1000).toFixed(2)}秒` : '-'}</div>
          </div>
        )}
      </div>

      {hasLeft ? (
        // 退室後の表示（みまもり会議専用）
        <Card style={{ maxWidth: 500, margin: '0 auto' }}> {/* StandardMeetingと共通のスタイル */}
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ color: leftReason === 'error' ? '#ff4d4f' : '#52c41a' }}> {/* 理由に応じた色分け */}
              {leftReason === 'error' ? '⚠️ エラーが発生しました' : '✅ みまもり終了'}
            </Title>
            <div>
              <p>緊急みまもり会議を終了しました。</p>
              <p>お疲れさまでした。</p>
            </div>
            <Space style={{ marginTop: 16 }}>
              <Button 
                type="primary" 
                onClick={() => window.close()}
              >
                ウィンドウを閉じる
              </Button>
              {/* みまもり会議では通常、再参加ボタンは設けない場合が多いが、要件による */}
              {/* <Button type="default" onClick={() => { ... }}>再参加</Button> */}
            </Space>
          </div>
        </Card>
      ) : (
        <>
          {/* 入室前の名前入力画面（みまもり会議では自動入室が基本のため、skipNameInputで制御） */}
          {!joined && !skipNameInput && (
            <Card style={{ maxWidth: 400 }}> {/* StandardMeetingと共通のスタイル */}
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input
                  placeholder="あなたの名前"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isJoining}
                />
                <Button
                  type="primary"
                  danger
                  block
                  onClick={joinMeeting}
                  loading={isJoining}
                  disabled={isJoining || !name.trim()}
                > {/* みまもり特有のスタイル */}
                  緊急みまもり会議に参加
                </Button>
              </Space>
            </Card>
          )}

          {/* 会議参加中の画面（みまもり会議専用UI） */}
          {joined && (
            <div style={mainAreaStyle}> {/* mainAreaStyleを適用 */}
              {/* 左側: メイン画面 (80%) */}
              <div
                style={{
                  flex: isWideLayout ? 8 : 'none', 
                  width: isWideLayout ? 'auto' : '100%', // StandardMeetingと共通
                  backgroundColor: '#000',
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0px', // StandardMeetingと共通
                  overflow: 'hidden',                  
                }}
              >
                <div
                  ref={mainVideoRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    backgroundColor: '#000', // StandardMeetingと共通
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                />
                
                {/* 緊急マーク（メイン画面左上）*/}
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    padding: '5px 10px',
                    backgroundColor: 'rgba(255, 77, 79, 0.9)', // みまもり特有
                    color: '#fff',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  🚨「 {speakingUser ? getParticipantName(speakingUser, participants) : '利用者'}」さんのカメラ映像
                </div>
                
                {/* メイン画面に参加者がいない場合の表示 */}
                {!speakingUser && ( // StandardMeetingにも同様の表示あり
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#000',
                      color: '#fff',
                      fontSize: '18px',
                    }}
                  >
                    みまもり対象者の接続を待機中... {/* みまもり特有のテキスト */}
                  </div>
                )}
                
                {/* メイン表示者がカメラオフの場合の表示 */}
                {speakingUser && !remoteVideoStates[speakingUser] && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#111',
                      color: '#fff',
                    }}
                  >
                    <img 
                      src={cameraOffIcon} 
                      alt="カメラオフ" 
                      style={{ 
                        width: '128px', 
                        height: '128px', 
                        marginBottom: '16px' 
                      }}
                    /> 
                    <div style={{ fontSize: '24px' }}>
                      {getParticipantName(speakingUser, participants)}
                      {!remoteAudioStates[speakingUser] && (
                        <span style={{ color: 'red', marginLeft: '10px' }}>（音声オフ）</span>
                      )}
                    </div>
                  </div>
                )}            
              </div>

              {/* 右側: 参加者一覧（みまもり会議専用） */}
              <div
                style={{
                  flex: isWideLayout ? 2 : 1,
                  width: isWideLayout ? 'auto' : '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: '4px',
                  backgroundColor: '#f0f0f0',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {/* 参加者セクション */}
                <div>
                  <Title level={5} style={{ 
                    margin: '0px 0', // Standardと共通
                    wordBreak: 'break-word', whiteSpace: 'normal', overflow: 'hidden', // Standardと共通
                    color: '#ff4d4f' // みまもり特有
                  }}>
                    みまもり参加者
                  </Title>
                  
                  {/* 自分 (みまもり管理者) */}
                  <Card
                    title={`自分 (${name})`} // みまもり特有のタイトル
                    style={{
                      marginBottom: '2px', // Standardと共通
                      width: isMobilePortrait ? '50%' : '100%', // Standardと共通
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      wordBreak: 'break-word',
                    }}
                    bodyStyle={{ padding: '4px' }} // Standardと共通
                  >
                    <div
                      ref={localVideoRef}
                      style={{
                        width: '100%', aspectRatio: '16 / 9', // Standardと共通
                        backgroundColor: '#111', position: 'relative', // Standardと共通
                      }}
                    >
                      {isVideoOff && ( // Standardと共通のロジックとスタイル
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#111',
                            color: '#fff',
                          }}
                        >
                          <img src={cameraOffIcon} alt="カメラオフ" style={{ 
                              width: '64px', 
                              height: '64px', 
                              marginBottom: '8px' 
                            }} 
                          />
                        </div>
                      )}
                      {/* 音声オフの場合はオーバーレイ表示 */}
                      {isMuted && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '5px',
                            left: '5px',
                            padding: '4px 8px',
                            backgroundColor: 'rgba(255, 0, 0, 0.7)',
                            color: '#fff',
                            fontSize: '12px',
                            borderRadius: '4px',
                            pointerEvents: 'none',
                            zIndex: 2,
                          }}
                        >
                          音声オフ
                        </div>
                      )}
                    </div>
                  </Card>
                  
                  {/* リモートユーザー（みまもり対象者） */}
                  {remoteUsers.map((user) => (
                    <Card
                      key={user.uid}
                      title={getParticipantName(String(user.uid), participants)}
                      style={{
                        marginBottom: '2px',
                        width: isMobilePortrait ? '50%' : '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        wordBreak: 'break-word',
                      }}
                      bodyStyle={{ padding: '4px' }} 
                    >
                      <div style={{ position: 'relative' }}> 
                        {/* ビデオコンテナ */}
                        <div
                        ref={(el) => { 
                            if (el) {
                            remoteVideoRefs.current.set(String(user.uid), el);
                            if (user.videoTrack && String(user.uid) !== speakingUser) {
                                // elは確実にHTMLDivElementなので直接渡す
                                playRemoteVideo(user, el);
                            }
                            }
                        }}
                        id={`remote-${user.uid}`}
                        style={{
                            width: '100%',
                            aspectRatio: '16 / 9',
                            backgroundColor: '#111',
                            position: 'relative',
                        }}
                        />         

                        {/* メインに表示中のオーバーレイ */}
                        {String(user.uid) === speakingUser && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              padding: '4px 8px',
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              color: '#fff',
                              fontSize: '12px',
                              borderRadius: '4px',
                              pointerEvents: 'none',
                            }}
                          >
                            メイン画面に<br />表示中
                          </div>
                        )}

                        {/* 音声オフの表示 */}
                        {!remoteAudioStates[user.uid] && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '5px',
                              left: '5px',
                              padding: '4px 8px',
                              backgroundColor: 'rgba(255, 0, 0, 0.7)',
                              color: '#fff',
                              fontSize: '12px',
                              borderRadius: '4px',
                              pointerEvents: 'none',
                              zIndex: 2,
                            }}
                          >
                            音声オフ
                          </div>
                        )}

                        {/* カメラオフの表示 */}
                        {!remoteVideoStates[user.uid] && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#111',
                              color: '#fff',
                              pointerEvents: 'none',
                            }}
                          >
                            <img src={cameraOffIcon} alt="カメラオフ" style={{ 
                                width: '64px', 
                                height: '64px', 
                                marginBottom: '8px' 
                              }} 
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>

                {/* 未参加者セクション (StandardMeetingと共通の構造とスタイル) */}
                <div>
                  <Title level={5} style={{ margin: '0px 0' }}>
                    過去参加した人
                  </Title>
                  {getNonParticipants(participants, remoteUsers).length > 0 ? (
                    getNonParticipants(participants, remoteUsers).map((participant) => (
                      <div
                        key={participant.uid}
                        style={{
                          width: isMobilePortrait ? '50%' : '100%',
                          maxWidth: '100%',
                          padding: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '4px',
                          marginBottom: '2px',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                          boxSizing: 'border-box',
                          wordBreak: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {participant.name} (UID: {participant.uid})
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        width: isMobilePortrait ? '50%' : '100%',
                        padding: '8px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        marginBottom: '2px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        color: '#888',
                      }}
                    >
                      未参加者なし
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgoraMimamoriMeeting;